import { useState, useMemo, useRef, useCallback, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '__components/ToastProvider';
import { monitoringMessageHelper } from 'instrumentation/helpers';
import styled from '@emotion/styled';
import css from '@emotion/css';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';
import { Typography } from '@kizen/kds/Typography';
import type { KnowledgeCatalogAskCitation } from '@kizen/api/knowledge';
import { InlineMarkdownText } from '__components/WYSIWYG/MarkdownEditor/components/InlineMarkdownText';
import IconAdornment from '__components/Inputs/Adornments/IconAdornment';
import LazyLoadList from '__components/Kizen/LazyLoad/LazyLoadList';
import { useEndsOfYScroll } from '__components/Kizen/LazyLoad/helpers';
import KnowledgeService from 'services/KnowledgeService';
import { downloadFile } from 'utility/DownloadFile';

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  min-height: 0;
`;

const RowWrapper = styled.div<{
  header?: boolean;
  useScrollbarPadding?: boolean;
}>`
  display: grid;
  grid-template-columns: 250px auto;
  column-gap: 10px;
  ${({ header }) =>
    header
      ? css`
          height: 20px;
        `
      : css`
          min-height: 40px;
          padding: 6px 0;
        `}
  ${({ useScrollbarPadding }) =>
    useScrollbarPadding
      ? css`
          padding-right: 10px;
        `
      : ''}
`;

const RowCell = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;

  &:first-child {
    padding-left: 15px;
  }

  &:last-child {
    padding-right: 25px;
    padding-left: 15px;
  }
`;

const MarkdownCellWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  min-width: 0;
`;

const SearchIconWrapper = styled.div`
  margin-left: 7px;
  padding-bottom: 7px;
  flex-shrink: 0;
  cursor: pointer;
`;

const ReadonlyMarkdownCell: FC<{
  value: string;
  'data-qa-id'?: string;
}> = ({ value, 'data-qa-id': dataQaId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const handleOpen = () => setIsOpen(true);

  return (
    <MarkdownCellWrapper data-qa-id={dataQaId}>
      <InlineMarkdownText
        isMarkdown
        value={value}
        isOpen={isOpen}
        onOpen={() => {}}
        onHide={() => setIsOpen(false)}
        onSave={() => {}}
        readonly
      />
      {value?.trim() ? (
        <SearchIconWrapper onClick={handleOpen}>
          <IconAdornment icon="search" />
        </SearchIconWrapper>
      ) : null}
    </MarkdownCellWrapper>
  );
};

type CitationRecord = {
  relevance_score: number;
  source_url?: string;
  source_id?: string;
  chunk_id: string;
  source_description: string;
  chunk_content: string;
};

type SourceCitationsTableProps = {
  citations: KnowledgeCatalogAskCitation[];
  catalogId: string;
};

export const SourceCitationsTable = ({
  citations,
  catalogId,
}: SourceCitationsTableProps) => {
  const { t } = useTranslation();
  const [showToast, toastVariant] = useToast();
  const scrollContainer = useRef<HTMLDivElement>(null);
  const typedCitations = citations as unknown as CitationRecord[];
  const sorted = useMemo(
    () =>
      [...typedCitations].sort((a, b) => b.relevance_score - a.relevance_score),
    [typedCitations]
  );

  const [scrolledTop, scrolledBottom] = useEndsOfYScroll(scrollContainer, [
    sorted,
  ]);
  const isScrollable = !scrolledTop || !scrolledBottom;

  const handleSourceClick = useCallback(
    async (e: React.MouseEvent, citation: CitationRecord) => {
      e.preventDefault();

      if (citation.source_url) {
        window.open(citation.source_url, '_blank', 'noopener noreferrer');
        return;
      }

      if (!citation.source_id) return;

      try {
        const blob = await KnowledgeService.downloadKnowledgeSource(
          citation.source_id,
          { params: { catalog_id: catalogId } }
        );
        const objectUrl = URL.createObjectURL(blob);
        downloadFile(objectUrl, citation.source_description || '');
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
      } catch (err) {
        monitoringMessageHelper(
          err instanceof Error
            ? err.message
            : 'Failed to download citation source',
          { extra: { source_id: citation.source_id } }
        );
        showToast({
          message: t('Failed to open source. Please try again.'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [catalogId, showToast, toastVariant, t]
  );

  return (
    <TableContainer>
      <RowWrapper header useScrollbarPadding={isScrollable}>
        <RowCell>
          <Typography size="md" weight="semibold">
            {t('Source')}
          </Typography>
        </RowCell>
        <RowCell>
          <Typography size="md" weight="semibold">
            {t('Search Excerpt')}
          </Typography>
        </RowCell>
      </RowWrapper>
      <LazyLoadList
        data={sorted}
        onScrollThreshold={() => {}}
        fetchingNextPage={false}
        loading={false}
        scrollContainerRef={scrollContainer}
        kdsCompat
      >
        {sorted.map((citation) => (
          <RowWrapper
            key={citation.chunk_id}
            useScrollbarPadding={isScrollable}
          >
            <RowCell>
              <TextEllipsisWithTooltip
                type="anchor"
                href={citation.source_url ?? ''}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                  handleSourceClick(e, citation)
                }
                data-qa-id={`citation-source-${citation.chunk_id}`}
              >
                {citation.source_description}
              </TextEllipsisWithTooltip>
            </RowCell>
            <RowCell>
              <ReadonlyMarkdownCell
                value={citation.chunk_content}
                data-qa-id={`citation-excerpt-${citation.chunk_id}`}
              />
            </RowCell>
          </RowWrapper>
        ))}
      </LazyLoadList>
    </TableContainer>
  );
};
